'use client'

import { useMemo, useState } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import { formatDateTime } from '@/lib/utils'
import {
  MoreHorizontalIcon,
  EyeIcon,
  EditIcon,
  Trash2Icon,
  SquarePlusIcon,
  ShieldIcon,
  UsersIcon,
  CalendarIcon,
  ShieldUserIcon,
  RefreshCwIcon
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { DataTablePagination } from '@/components/ui/data-table-pagination'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu'
import { Checkbox } from '@/components/ui/checkbox'
import { DeleteConfirmationDialog, SortableHeader, LoadingState, GroupsFilters } from '@/components/partials'
import { GroupsFilterParams } from '@/types/group'
import { RoleEnum, SortEnum } from '@/enums'
import { useGroups, useDeleteGroup, useBulkDeleteGroups } from '@/hooks'
import { useGroupsStore, useMeStore } from '@/store'
import { hasPermission } from '@/lib/permissions'

const List = () => {
  const locale = useLocale()
  const t = useTranslations()
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryClient = useQueryClient()
  const [deleteGroupId, setDeleteGroupId] = useState<string | null>(null)
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState<boolean>(false)

  // Group selection hooks from Zustand store
  const { meState } = useMeStore()
  const currentUser = meState.response
  const canCreate = hasPermission(currentUser, 'GROUPS', 'CREATE')
  const canShow = hasPermission(currentUser, 'GROUPS', 'SHOW')
  const canUpdate = hasPermission(currentUser, 'GROUPS', 'UPDATE')
  const canDelete = hasPermission(currentUser, 'GROUPS', 'DELETE')

  const selectedGroupIds = useGroupsStore((state: any) => state.selectedGroupIds)
  const toggleGroupSelection = useGroupsStore((state: any) => state.toggleGroupSelection)
  const selectAllGroups = useGroupsStore((state: any) => state.selectAllGroups)
  const deselectAllGroups = useGroupsStore((state: any) => state.deselectAllGroups)
  const isGroupSelected = useGroupsStore((state: any) => state.isGroupSelected)

  // Bulk delete mutation from hook
  const { mutate: bulkDeleteGroupsMutate, isPending: isBulkDeleting } = useBulkDeleteGroups()

  const filters = useMemo<GroupsFilterParams>(() => {
    const params = new URLSearchParams(searchParams.toString())

    return {
      page: parseInt(params.get('page') || '1'),
      size: parseInt(params.get('size') || '20'),
      sort: (params.get('sort') as SortEnum) || SortEnum.ASC,
      sortBy: params.get('sortBy') || 'createdAt',
      q: params.get('q') || '',
      roles: params.get('roles') ? (params.get('roles')!.split(',') as RoleEnum[]) : undefined
    }
  }, [searchParams])

  const { data: groupsData, isLoading, error, refetch, isFetching } = useGroups(filters)
  const { mutate: deleteGroupMutate, isPending: isDeleting } = useDeleteGroup()

  // Update URL when filters change
  const updateUrlParamsManual = (newFilters: Partial<GroupsFilterParams>) => {
    const params = new URLSearchParams()
    const updatedFilters = { ...filters, ...newFilters }

    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (key === 'roles' && Array.isArray(value)) {
          if (value.length > 0) {
            params.set(key, value.join(','))
          }
        } else {
          params.set(key, value.toString())
        }
      }
    })

    router.push(`?${params.toString()}`)
  }

  const handleFiltersChange = (newFilters: Partial<GroupsFilterParams>) => {
    updateUrlParamsManual(newFilters)
  }

  const handlePageChange = (page: number) => {
    handleFiltersChange({ page })
  }

  const handlePageSizeChange = (size: number) => {
    handleFiltersChange({ size, page: 1 })
  }

  const handleResetFilters = () => {
    router.push('/users/groups')
  }

  const handleSort = (sortBy: string) => {
    const newSort = filters.sortBy === sortBy && filters.sort === SortEnum.ASC ? SortEnum.DESC : SortEnum.ASC
    handleFiltersChange({ sortBy, sort: newSort })
  }

  const handleViewGroup = (groupId: string) => {
    router.push(`/users/groups/${groupId}`)
  }

  const handleEditGroup = (groupId: string) => {
    router.push(`/users/groups/${groupId}/edit`)
  }

  const confirmDeleteGroup = () => {
    if (deleteGroupId) {
      deleteGroupMutate(deleteGroupId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['groups'] })
          toast.success(t('deleted_successfully'))
          setDeleteGroupId(null)
        },
        onError: (error: any) => {
          console.error('Error deleting group:', error)
          setDeleteGroupId(null)
        }
      })
    }
  }

  const handleBulkDelete = () => {
    if (selectedGroupIds.length > 0) {
      setBulkDeleteConfirmOpen(true)
    }
  }

  const confirmBulkDeleteGroups = () => {
    if (selectedGroupIds.length > 0) {
      bulkDeleteGroupsMutate(
        { ids: selectedGroupIds },
        {
          onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['groups'] })
            setBulkDeleteConfirmOpen(false)
          },
          onError: (error: any) => {
            console.error('Error bulk deleting groups:', error)
            setBulkDeleteConfirmOpen(false)
          }
        }
      )
    }
  }

  const renderActionItems = (groupId: string, inDropdown = true) => {
    const items = [
      ...(canShow
        ? [
            {
              label: t('user_group_details'),
              icon: EyeIcon,
              onClick: () => handleViewGroup(groupId)
            }
          ]
        : []),
      ...(canUpdate
        ? [
            {
              label: t('edit_user_group'),
              icon: EditIcon,
              onClick: () => handleEditGroup(groupId)
            }
          ]
        : []),
      ...(canDelete
        ? [
            {
              label: t('delete'),
              icon: Trash2Icon,
              onClick: () => setDeleteGroupId(groupId),
              className: 'text-red-600 hover:text-red-700'
            }
          ]
        : [])
    ]

    if (inDropdown) {
      return items.map(item => (
        <DropdownMenuItem
          key={item.label}
          className={item.className}
          onSelect={e => {
            e.preventDefault()
            e.stopPropagation()
            item.onClick()
          }}
          onClick={e => {
            e.preventDefault()
            e.stopPropagation()
            item.onClick()
          }}>
          <item.icon className="mr-2 h-4 w-4" />
          {item.label}
        </DropdownMenuItem>
      ))
    }

    return items.map(item => (
      <ContextMenuItem
        key={item.label}
        className={item.className}
        onSelect={e => {
          e.preventDefault()
          e.stopPropagation()
          item.onClick()
        }}
        onClick={e => {
          e.preventDefault()
          e.stopPropagation()
          item.onClick()
        }}>
        <item.icon className="mr-2 h-4 w-4" />
        {item.label}
      </ContextMenuItem>
    ))
  }

  // Handle select all checkbox
  const handleSelectAll = (checked: boolean) => {
    if (checked && groupsData?.items) {
      selectAllGroups(groupsData.items)
    } else {
      deselectAllGroups()
    }
  }

  const allSelected =
    groupsData?.items && groupsData.items.length > 0 && selectedGroupIds.length === groupsData.items.length

  if (isLoading) {
    return <LoadingState isLoading={true}>Loading groups...</LoadingState>
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('error')}</CardTitle>
          <CardDescription>{t('error_loading_record')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => window.location.reload()}>{t('try_again')}</Button>
        </CardContent>
      </Card>
    )
  }

  if (!groupsData) {
    return null
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="grid gap-3 sm:flex sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ShieldUserIcon className="h-5 w-5" />
                {t('groups')}
              </CardTitle>
              <CardDescription>{t('groups_description')}</CardDescription>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching || isLoading}>
                <RefreshCwIcon className={`h-4 w-4${isFetching ? 'animate-spin' : ''}`} />
              </Button>
              {canCreate && (
                <Button asChild>
                  <Link href="/users/groups/create">
                    <SquarePlusIcon />
                    <span className="hidden lg:flex">{t('create_user_group')}</span>
                  </Link>
                </Button>
              )}
              {canDelete && selectedGroupIds.length > 0 && (
                <Button variant="destructive" onClick={handleBulkDelete} disabled={isBulkDeleting}>
                  <Trash2Icon className="h-4 w-4" />
                  <div className="hidden items-center gap-2 lg:flex">
                    <span>{t('delete_selected')}</span>
                    <span>({selectedGroupIds.length})</span>
                  </div>
                </Button>
              )}
              <GroupsFilters
                filters={filters}
                onFiltersChangeAction={handleFiltersChange}
                onResetFiltersAction={handleResetFilters}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <LoadingState
            isLoading={isLoading}
            error={error as any}
            onRetry={() => window.location.reload()}
            loadingRows={5}>
            {groupsData && (
              <div className="space-y-4">
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12.5 text-center">
                          {canDelete && (
                            <Checkbox
                              checked={allSelected}
                              onCheckedChange={handleSelectAll}
                              className="cursor-pointer"
                            />
                          )}
                        </TableHead>
                        <TableHead>
                          <SortableHeader
                            sortBy="name"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            {t('name')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="role"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <div className="flex items-center justify-center gap-2">
                              <ShieldIcon className="h-4 w-4" />
                              {t('role')}
                            </div>
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-37.5 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <UsersIcon className="h-4 w-4" />
                            {t('users')}
                          </div>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="createdAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <div className="flex items-center justify-center gap-2">
                              <CalendarIcon className="h-4 w-4" />
                              {t('created_at')}
                            </div>
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="updatedAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <div className="flex items-center justify-center gap-2">
                              <CalendarIcon className="h-4 w-4" />
                              {t('updated_at')}
                            </div>
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="w-25 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <MoreHorizontalIcon className="h-4 w-4" />
                            {t('actions')}
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {groupsData.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="h-24 text-center">
                            {t('no_results')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        groupsData.items.map((group: any, index: number) => (
                          <ContextMenu key={group.id}>
                            <ContextMenuTrigger asChild>
                              <TableRow
                                className={`hover:bg-muted/50 cursor-pointer transition-colors ${
                                  index % 2 === 0 ? 'bg-background' : 'bg-muted/25'
                                }`}
                                onClick={() => canShow && handleViewGroup(group.id)}>
                                <TableCell className="text-center">
                                  <div className="flex items-center justify-center">
                                    {canDelete && (
                                      <Checkbox
                                        checked={isGroupSelected(group.id)}
                                        onCheckedChange={() => toggleGroupSelection(group.id)}
                                        onClick={e => {
                                          e.stopPropagation()
                                        }}
                                        className="cursor-pointer"
                                      />
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-col">
                                    <span>{group.name}</span>
                                    <span className="text-muted-foreground text-xs">ID: {group.id}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge variant="outline">{t(group.role)}</Badge>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="text-muted-foreground text-sm">
                                    <span>{group.userCount}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="text-muted-foreground text-sm">
                                    <span>{formatDateTime(group.createdAt, locale)}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="text-muted-foreground text-sm">
                                    <span>{formatDateTime(group.updatedAt, locale)}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={e => {
                                          e.preventDefault()
                                          e.stopPropagation()
                                        }}>
                                        <span className="sr-only">{t('open_menu')}</span>
                                        <MoreHorizontalIcon className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {renderActionItems(group.id, true)}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            </ContextMenuTrigger>
                            <ContextMenuContent>{renderActionItems(group.id, false)}</ContextMenuContent>
                          </ContextMenu>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                {groupsData.pages > 1 && (
                  <DataTablePagination
                    currentPage={groupsData.page}
                    totalPages={groupsData.pages}
                    pageSize={filters.size || 20}
                    totalItems={groupsData.total}
                    onPageChangeAction={handlePageChange}
                    onPageSizeChangeAction={handlePageSizeChange}
                  />
                )}
              </div>
            )}
          </LoadingState>
        </CardContent>
      </Card>

      {canDelete && (
        <DeleteConfirmationDialog
          open={!!deleteGroupId}
          onCancel={() => setDeleteGroupId(null)}
          onConfirm={confirmDeleteGroup}
          isDeleting={isDeleting}
        />
      )}

      {canDelete && (
        <DeleteConfirmationDialog
          open={bulkDeleteConfirmOpen}
          onCancel={() => setBulkDeleteConfirmOpen(false)}
          onConfirm={confirmBulkDeleteGroups}
          isDeleting={isBulkDeleting}
          description={t('bulk_delete_confirmation_message')}
        />
      )}
    </>
  )
}

export default List
